import type { User } from '@data-hub/shared';
import { createFileRoute } from '@tanstack/react-router';
import {
  BanIcon,
  CheckIcon,
  MoreHorizontalIcon,
  PencilIcon,
  ShieldCheckIcon,
  ShieldOffIcon,
  TrashIcon,
  XIcon,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { AdminGuard } from '@/components/layout/AdminGuard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useMe } from '@/features/auth/use-me';
import { DeleteUserDialog } from '@/features/users/DeleteUserDialog';
import { EditUserDialog } from '@/features/users/EditUserDialog';
import { useUpdateUser, useUsers } from '@/features/users/use-users';
import { handleApiError } from '@/lib/handle-error';

export const Route = createFileRoute('/_authed/admin/users')({
  component: AdminUsersPage,
});

function AdminUsersPage() {
  return (
    <AdminGuard>
      <UsersAdmin />
    </AdminGuard>
  );
}

function UsersAdmin() {
  const users = useUsers();
  const me = useMe();
  const update = useUpdateUser();
  const [editTarget, setEditTarget] = useState<User | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<User | null>(null);
  const items = users.data?.items ?? [];
  const myEmail = me.data?.email?.toLowerCase() ?? null;

  const toggleActive = (u: User) => {
    update.mutate(
      { id: u.id, input: { isActive: !u.isActive } },
      {
        onSuccess: () => {
          toast.success(u.isActive ? 'User deactivated' : 'User reactivated');
        },
        onError: (err) => handleApiError(err, 'Failed to update user'),
      },
    );
  };

  const toggleReviewer = (u: User) => {
    const next = !u.reviewer;
    update.mutate(
      { id: u.id, input: { reviewer: next } },
      {
        onSuccess: () => {
          if (next && u.area !== 'DATA') {
            // Set the flag anyway (admin's call), but make it clear the
            // user won't actually be effective as a reviewer until their
            // area is DATA. Avoids silent dead-ends.
            toast.warning('Flag set, but only effective when this user is in the DATA area.');
          } else {
            toast.success(next ? 'Promoted to reviewer' : 'Reviewer flag removed');
          }
        },
        onError: (err) => handleApiError(err, 'Failed to update reviewer flag'),
      },
    );
  };

  return (
    <div className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-4 py-8 sm:px-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Users</h1>
        <p className="text-sm text-muted-foreground">Manage roles and access.</p>
      </div>

      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead>Display name</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Area</TableHead>
              <TableHead className="w-[100px]">Reviewer</TableHead>
              <TableHead className="w-[100px]">Active</TableHead>
              <TableHead className="w-[60px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.isPending ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={7}>
                    <Skeleton className="h-6 w-full" />
                  </TableCell>
                </TableRow>
              ))
            ) : items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="py-12 text-center text-muted-foreground">
                  No users found.
                </TableCell>
              </TableRow>
            ) : (
              items.map((u) => (
                <TableRow key={u.id}>
                  <TableCell className="font-mono text-xs">{u.email}</TableCell>
                  <TableCell>{u.displayName ?? '—'}</TableCell>
                  <TableCell>
                    <Badge variant={u.role === 'admin' ? 'default' : 'secondary'}>{u.role}</Badge>
                  </TableCell>
                  <TableCell>{u.area ?? '—'}</TableCell>
                  <TableCell>
                    {u.reviewer ? (
                      <Badge
                        variant={u.area === 'DATA' ? 'default' : 'outline'}
                        className="gap-1 text-[10px]"
                        title={
                          u.area === 'DATA'
                            ? 'Can approve Metric Hub drafts'
                            : 'Flag set, but only effective when area is DATA'
                        }
                      >
                        <ShieldCheckIcon className="size-3" /> Reviewer
                      </Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {u.isActive ? (
                      <CheckIcon className="size-4 text-emerald-500" />
                    ) : (
                      <XIcon className="size-4 text-muted-foreground" />
                    )}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" aria-label="Actions" className="size-8">
                          <MoreHorizontalIcon className="size-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-52">
                        <DropdownMenuItem onClick={() => setEditTarget(u)}>
                          <PencilIcon className="size-4" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          disabled={update.isPending}
                          onClick={() => toggleReviewer(u)}
                        >
                          {u.reviewer ? (
                            <>
                              <ShieldOffIcon className="size-4" />
                              Revoke reviewer
                            </>
                          ) : (
                            <>
                              <ShieldCheckIcon className="size-4" />
                              Make reviewer
                            </>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          disabled={u.email.toLowerCase() === myEmail || update.isPending}
                          onClick={() => toggleActive(u)}
                        >
                          {u.isActive ? (
                            <>
                              <BanIcon className="size-4" />
                              Deactivate
                            </>
                          ) : (
                            <>
                              <CheckIcon className="size-4" />
                              Reactivate
                            </>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          variant="destructive"
                          disabled={u.email.toLowerCase() === myEmail}
                          onClick={() => setDeleteTarget(u)}
                        >
                          <TrashIcon className="size-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <EditUserDialog
        key={editTarget?.id ?? 'edit'}
        user={editTarget}
        open={editTarget !== null}
        onOpenChange={(open) => !open && setEditTarget(null)}
      />
      <DeleteUserDialog
        user={deleteTarget}
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      />
    </div>
  );
}
